import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/constants";
import {
  LayoutDashboard, Users2, KanbanSquare, CalendarClock,
  HeartPulse, Building2, Network, BarChart3, ShieldCheck,
  ScrollText, Plug, LogOut, ListChecks, Stethoscope, FileText, CalendarRange, ClipboardList,
  MessageCircle, PackageIcon, PhoneOutgoing, Percent, Send, Radar,
} from "lucide-react";

// Roles with full pre-sale (lead pipeline) access. Reception is NOT in here —
// they get the Enquiries entry below instead, which is the same Leads page
// scoped to their branch. They capture walk-ins and direct calls; they don't
// work the pipeline, so Pipeline / Follow-ups / WhatsApp / Tasks stay hidden.
const PRE_SALE_ROLES = [
  "super_admin", "hq_admin", "branch_manager", "franchisee_owner", "telecaller", "doctor",
];

// Department visibility. `dept` is the department(s) that can see a nav item;
// users whose department is "both" (or admins) see everything.
const preSalesDept = (dept) => dept === "pre_sales" || dept === "both";
const postSalesDept = (dept) => dept === "post_sales" || dept === "both";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/leads", label: "Leads", icon: Users2, roles: PRE_SALE_ROLES, dept: "pre_sales" },
  // Same page, reception's name for it. A separate entry rather than widening
  // the one above, so nothing changes for any existing role. The backend scopes
  // them to their own branch (routes_leads._scope_query).
  { to: "/leads", label: "Enquiries", icon: Users2, roles: ["receptionist"] },
  { to: "/pipeline", label: "Pipeline", icon: KanbanSquare, roles: PRE_SALE_ROLES, dept: "pre_sales" },
  { to: "/followups", label: "Follow-ups", icon: CalendarClock, roles: PRE_SALE_ROLES, dept: "pre_sales" },
  { to: "/whatsapp", label: "WhatsApp", icon: MessageCircle, roles: PRE_SALE_ROLES, dept: "pre_sales" },
  { to: "/tasks", label: "Tasks", icon: ListChecks, roles: PRE_SALE_ROLES, dept: "pre_sales" },
  { to: "/doctor/my-day", label: "My Day", icon: Stethoscope, roles: ["doctor"] },
  { to: "/calendar", label: "Calendar", icon: CalendarRange, dept: "post_sales" },
  { to: "/appointments", label: "Appointments", icon: ClipboardList },
  { to: "/patients", label: "Patients", icon: HeartPulse, dept: "post_sales" },
  { to: "/recalls", label: "Recalls", icon: PhoneOutgoing, dept: "post_sales" },
  { to: "/invoices", label: "Invoices", icon: FileText, dept: "post_sales" },
  { to: "/branches", label: "Branches", icon: Building2, roles: ["super_admin", "hq_admin", "branch_manager", "franchisee_owner"] },
  { to: "/franchises", label: "Franchises", icon: Network, roles: ["super_admin", "hq_admin"] },
  { to: "/reports", label: "Reports", icon: BarChart3, roles: ["super_admin", "hq_admin", "branch_manager", "franchisee_owner"] },
  // Lead performance per source / sub-source / ingestion integration. Same role set
  // as Reports — the endpoint 403s for anyone else.
  { to: "/performance-source", label: "Performance Source", icon: Radar, roles: ["super_admin", "hq_admin", "branch_manager", "franchisee_owner"] },
  { to: "/ops-reports", label: "Clinic Reports", icon: ClipboardList, roles: ["super_admin", "hq_admin", "branch_manager", "franchisee_owner", "receptionist"], dept: "post_sales" },
  { to: "/users", label: "Access Control", icon: ShieldCheck, roles: ["super_admin", "hq_admin", "branch_manager", "franchisee_owner"] },
  { to: "/audit", label: "Audit Logs", icon: ScrollText, roles: ["super_admin", "hq_admin"] },
  { to: "/integrations", label: "Integrations", icon: Plug, roles: ["super_admin", "hq_admin"] },
  { to: "/packages", label: "Packages", icon: PackageIcon, roles: ["super_admin", "hq_admin", "branch_manager", "receptionist"] },
  { to: "/communications", label: "Patient Messaging", icon: Send, roles: ["super_admin", "hq_admin", "branch_manager"], dept: "post_sales" },
  { to: "/clinic-settings", label: "Clinic Settings", icon: Percent, roles: ["super_admin", "hq_admin", "branch_manager", "receptionist"] },
  { to: "/master-data", label: "Master Data", icon: ListChecks, roles: ["super_admin", "hq_admin"] },
];

export default function Sidebar({ onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const dept = user?.department || "both";
  const items = NAV.filter((n) => {
    if (n.roles && !n.roles.includes(user?.role)) return false;
    if (n.dept === "pre_sales" && !preSalesDept(dept)) return false;
    if (n.dept === "post_sales" && !postSalesDept(dept)) return false;
    return true;
  });

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <aside className="flex h-full w-64 flex-col bg-navy text-white">
      <div className="px-5 py-6">
        <div className="rounded-lg bg-white px-3 py-2.5 shadow-sm">
          <img src="/logo.png" alt="Dermatrix" className="h-8 w-full object-contain" />
        </div>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-2 sidebar-scroll">
        {items.map((item) => (
          <NavLink
            key={`${item.to}-${item.label}`}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
                isActive
                  ? "bg-gold text-white"
                  : "text-white/70 hover:bg-white/10 hover:text-white"
              }`
            }
          >
            <item.icon className="h-[18px] w-[18px]" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-white/10 px-4 py-4">
        <div className="mb-3 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gold/20 text-sm font-semibold text-gold">
            {user?.name?.charAt(0) || "U"}
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{user?.name}</p>
            <p className="truncate text-xs text-white/60">{ROLE_LABELS[user?.role] || user?.role}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-white/70 transition-colors hover:bg-white/10 hover:text-white"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </aside>
  );
}
